import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, X, ExternalLink, Film, ChevronDown, Clapperboard } from 'lucide-react';
import { ApiVideo } from '@/hooks/useSiteContent';
import { api, mediaUrl } from '@/lib/api';
import { audioBus } from '@/lib/audioBus';
import { jukeboxBus } from '@/lib/jukeboxBus';
import { scrollToSection } from '@/lib/scroll';

const LOGO_WEBP = '/assets/brand/ekuba-logo-sm.webp';
const LOGO_PNG = '/assets/brand/ekuba-logo-sm.png';
const INITIAL = 6;

const PROVIDER_LABEL: Record<string, string> = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  dailymotion: 'Dailymotion',
  upload: 'Original',
  file: 'Video',
  other: 'External',
};

function LogoPlaceholder({ small }: { small?: boolean }) {
  return (
    <span className={`ek-vid-logo ${small ? 'is-small' : ''}`} aria-hidden="true">
      <picture>
        <source srcSet={LOGO_WEBP} type="image/webp" />
        <img src={LOGO_PNG} alt="" draggable={false} />
      </picture>
    </span>
  );
}

function Thumb({ v }: { v: ApiVideo }) {
  const [broken, setBroken] = useState(false);
  if (v.thumbnail_url && !broken) {
    return <img className="ek-vid-img" src={mediaUrl(v.thumbnail_url)} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} />;
  }
  if (v.source === 'upload' && v.video_url) {
    // first frame of the uploaded file as a poster
    return <video className="ek-vid-img" src={`${mediaUrl(v.video_url)}#t=0.8`} preload="metadata" muted playsInline aria-hidden="true" tabIndex={-1} />;
  }
  return <LogoPlaceholder />;
}

const metaLine = (v: ApiVideo) => [v.type, v.year].filter(Boolean).join(' • ').toUpperCase();
const playable = (v: ApiVideo) => v.source === 'upload' || !!v.embed_url || v.provider === 'file';

function VideoLightbox({ v, onClose }: { v: ApiVideo; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // count the open as a view (server de-dupes repeat opens from the same visitor)
    api.post(`/public/videos/${v.id}/view`).catch(() => {});
  }, [v.id]);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const ov = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // hush every other player while a video is on screen
    if (playable(v)) {
      audioBus.claim('video');
      jukeboxBus.fire({ type: 'pause' });
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = ov;
      prev?.focus?.();
    };
  }, [v, onClose]);

  let player: React.ReactNode;
  if (v.source === 'upload' && v.video_url) {
    player = (
      <video className="ek-vlb-media" src={mediaUrl(v.video_url)} controls autoPlay playsInline poster={v.thumbnail_url ? mediaUrl(v.thumbnail_url) : undefined} data-testid="video-lightbox-player">
        Your browser can’t play this video.
      </video>
    );
  } else if (v.embed_url) {
    player = (
      <iframe
        className="ek-vlb-media"
        src={v.embed_url}
        title={v.title}
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        data-testid="video-lightbox-player"
      />
    );
  } else if (v.provider === 'file' && v.url) {
    player = <video className="ek-vlb-media" src={v.url} controls autoPlay playsInline data-testid="video-lightbox-player" />;
  } else {
    player = (
      <div className="ek-vlb-empty" data-testid="video-lightbox-empty">
        <LogoPlaceholder />
        <p>{v.url ? 'This video plays on an external site.' : 'This video is coming soon.'}</p>
        {v.url && (
          <a className="ek-vlb-ext-btn" href={v.url} target="_blank" rel="noopener noreferrer" data-testid="video-lightbox-external-link">
            Watch now <ExternalLink size={14} />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="ek-vlb-backdrop" role="dialog" aria-modal="true" aria-label={`${v.title} video`} onClick={onClose} data-testid="video-lightbox">
      <div className="ek-vlb" onClick={(e) => e.stopPropagation()}>
        <div className="ek-vlb-frame">{player}</div>
        <div className="ek-vlb-info">
          <div className="min-w-0">
            <p className="ek-vlb-kicker" data-testid="video-lightbox-meta">
              {metaLine(v)}
              {v.album ? <span> · {v.album}</span> : null}
            </p>
            <h3 className="ek-vlb-title" data-testid="video-lightbox-title">{v.title}</h3>
            {v.artist && <p className="ek-vlb-artist">{v.artist}</p>}
            {v.description && <p className="ek-vlb-desc">{v.description}</p>}
          </div>
          <div className="ek-vlb-actions">
            {v.url && v.source === 'link' && (
              <a className="ek-vlb-ext" href={v.url} target="_blank" rel="noopener noreferrer" data-testid="video-lightbox-open-external">
                {PROVIDER_LABEL[v.provider] ? `Open on ${PROVIDER_LABEL[v.provider]}` : 'Open link'} <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
        <button ref={closeRef} type="button" className="ek-vlb-close" onClick={onClose} aria-label="Close video" data-testid="video-lightbox-close">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export default function FeaturedVideos({ videos, loaded }: { videos: ApiVideo[]; loaded: boolean }) {
  const [open, setOpen] = useState<ApiVideo | null>(null);
  const [showAll, setShowAll] = useState(false);
  const close = useCallback(() => {
    setOpen(null);
    // drop a ?video= deep link from the address bar once the lightbox closes
    const u = new URL(window.location.href);
    if (u.searchParams.has('video')) {
      u.searchParams.delete('video');
      window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    }
  }, []);
  const list = showAll ? videos : videos.slice(0, INITIAL);

  // ?video=<id> (used by the "new video" newsletter alert) opens that video straight away
  const deepLinked = useRef(false);
  useEffect(() => {
    if (!loaded || deepLinked.current) return;
    const id = new URLSearchParams(window.location.search).get('video');
    if (!id) return;
    deepLinked.current = true;
    const v = videos.find((x) => x.id === id);
    if (!v) return;
    const idx = videos.indexOf(v);
    if (idx >= INITIAL) setShowAll(true);
    setTimeout(() => scrollToSection('videos'), 150);
    setOpen(v);
  }, [loaded, videos]);

  return (
    <section id="videos" className="ek-videos" aria-labelledby="videos-title" data-testid="featured-videos-section">
      <div className="ek-videos-head">
        <h2 id="videos-title" className="ek-sec-title ek-videos-title">FEATURED VIDEOS</h2>
        {videos.length > 0 && (
          <span className="ek-videos-count" data-testid="featured-videos-count">
            {videos.length} VIDEO{videos.length === 1 ? '' : 'S'}
          </span>
        )}
      </div>

      {!loaded ? (
        <div className="ek-videos-grid" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ek-vid-card is-skeleton">
              <span className="ek-vid-thumb" />
              <span className="ek-vid-sk-line" />
              <span className="ek-vid-sk-line is-short" />
            </div>
          ))}
        </div>
      ) : videos.length === 0 ? (
        <div className="ek-videos-empty" data-testid="featured-videos-empty">
          <div className="ek-videos-empty-art">
            <LogoPlaceholder />
          </div>
          <div className="ek-videos-empty-copy">
            <p className="ek-videos-empty-kicker">
              <Clapperboard size={13} /> VIDEOS COMING SOON
            </p>
            <h3>Music videos, live sets &amp; studio sessions are on the way.</h3>
            <p>Join the newsletter and be the first to watch when they drop.</p>
            <button type="button" className="ek-videos-empty-btn" onClick={() => scrollToSection('newsletter')} data-testid="featured-videos-empty-subscribe">
              Get notified
            </button>
          </div>
        </div>
      ) : (
        <>
          <ul className="ek-videos-grid">
            {list.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  className={`ek-vid-card ${v.source === 'none' ? 'is-soon' : ''}`}
                  onClick={() => setOpen(v)}
                  aria-label={`${v.source === 'none' ? 'Details for' : 'Play'} ${v.title}`}
                  data-testid={`video-card-${v.id}`}
                >
                  <span className={`ek-vid-thumb ${v.thumbnail_url || (v.source === 'upload' && v.video_url) ? '' : 'has-logo'}`}>
                    <Thumb v={v} />
                    <span className="ek-vid-shade" aria-hidden="true" />
                    {v.source === 'none' ? (
                      <span className="ek-vid-badge is-soon">COMING SOON</span>
                    ) : (
                      PROVIDER_LABEL[v.provider] && <span className="ek-vid-badge">{PROVIDER_LABEL[v.provider]}</span>
                    )}
                    {v.source !== 'none' && (
                      <span className="ek-vid-play" aria-hidden="true">
                        <Play size={18} fill="currentColor" />
                      </span>
                    )}
                  </span>
                  <span className="ek-vid-meta">{metaLine(v) || 'VIDEO'}</span>
                  <span className="ek-vid-name">{v.title}</span>
                  {v.album && (
                    <span className="ek-vid-album">
                      <Film size={11} /> {v.album}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {videos.length > INITIAL && (
            <button type="button" className="ek-videos-more" onClick={() => setShowAll((s) => !s)} aria-expanded={showAll} data-testid="featured-videos-toggle-all">
              {showAll ? 'Show fewer' : `Show all ${videos.length} videos`}
              <ChevronDown size={14} className={showAll ? 'is-open' : ''} />
            </button>
          )}
        </>
      )}

      {open && <VideoLightbox v={open} onClose={close} />}
    </section>
  );
}
